import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, CheckCircleIcon, CreditCardIcon, ArrowRightIcon, HeartIcon, UsersIcon, TicketIcon, GraduationCapIcon, ZapIcon, ShieldCheckIcon, FlowerIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
function AnimatedCounter({
  target,
  duration = 2000,
  showPlus = true
}: {
  target: number;
  duration?: number;
  showPlus?: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number | null = null;
    let rafId: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);
  return <span data-id="element-4">
      {showPlus ? '+' : ''}
      {count.toLocaleString('es-AR')}
    </span>;
}
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode;
};
const Button = ({
  children,
  className = '',
  ...props
}: ButtonProps) => <button className={className} {...props} data-id="element-5">
    {children}
  </button>;
const PURCHASE_LINK = 'https://esenciaaurica.store/cart/44400455385130:1';
const ARG_CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Salta', 'Neuquén', 'Mar del Plata', 'San Miguel de Tucumán', 'La Plata', 'Santa Fe'];
const NAMES = ['María', 'Florencia', 'Lucía', 'Martina', 'Sofía', 'Valentina', 'Camila', 'Julieta', 'Paula', 'Agustina'];
const Lavender = ({
  className = '',
  delay = 0,
  scale = 1,
  rotate = 0
}: {
  className?: string;
  delay?: number;
  scale?: number;
  rotate?: number;
}) => <motion.div className={`absolute pointer-events-none z-0 ${className}`} initial={{
  rotate: rotate - 2
}} animate={{
  rotate: [rotate - 2, rotate + 2, rotate - 2]
}} transition={{
  duration: 5 + Math.random() * 2,
  repeat: Infinity,
  ease: 'easeInOut',
  delay
}} style={{
  transformOrigin: 'bottom center',
  scale
}} data-id="element-6">
    <svg width="60" height="180" viewBox="0 0 60 180" fill="none" data-id="element-7">
      {/* Stem */}
      <path d="M30 180C30 140 30 100 30 60" stroke="#5a7a4a" strokeWidth="1.5" strokeLinecap="round" opacity="0.8" data-id="element-8" />
      {/* Flower clusters */}
      {[...Array(10)].map((_, i) => <g key={i} opacity={1 - i * 0.05} data-id="element-9">
          <ellipse cx={30 + (i % 2 === 0 ? 3 : -3)} cy={20 + i * 10} rx="3.5" ry="6" fill={i % 3 === 0 ? '#b19cd9' : '#9b7ede'} transform={`rotate(${i % 2 === 0 ? 20 : -20} ${30 + (i % 2 === 0 ? 3 : -3)} ${20 + i * 10})`} data-id="element-10" />
          <ellipse cx={30 + (i % 2 === 0 ? -2 : 2)} cy={22 + i * 10} rx="2.5" ry="4" fill="#d8bfd8" opacity="0.6" data-id="element-11" />
        </g>)}
      {/* Small leaves */}
      <path d="M30 130C20 120 15 100 15 90" stroke="#5a7a4a" strokeWidth="1" strokeLinecap="round" opacity="0.4" data-id="element-12" />
      <path d="M30 150C40 140 45 120 45 110" stroke="#5a7a4a" strokeWidth="1" strokeLinecap="round" opacity="0.4" data-id="element-13" />
    </svg>
  </motion.div>;
function FaqItem({
  question,
  answer,
  index
}: {
  question: string;
  answer: string;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className="border-b border-rose-100/50" data-id="element-14">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left group" data-id="element-15">
        <span style={{
        fontFamily: "'Playfair Display', serif"
      }} className="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-[#b8865c] transition-colors duration-300" data-id="element-16">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} className="text-[#d4af37]" data-id="element-17">
          <ArrowRightIcon className="w-5 h-5 rotate-90" data-id="element-18" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-19">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-20">
            <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="pb-6 text-gray-600 text-lg leading-relaxed italic" data-id="element-21">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function EsenciaAurica() {
  const screenInit = useScreenInit();
  const [viewers, setViewers] = useState(48);
  const [spots, setSpots] = useState(13);
  const [joinedCount, setJoinedCount] = useState(1010);
  const [recentBuyer, setRecentBuyer] = useState<{
    name: string;
    city: string;
  } | null>(null);
  const [comments, setComments] = useState(INITIAL_COMMENTS);
  const [newComment, setNewComment] = useState('');
  const [newRating, setNewRating] = useState(5);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [visibleCommentsCount, setVisibleCommentsCount] = useState(5);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => Math.max(10, Math.min(15, prev + (Math.random() > 0.5 ? 1 : -1))));
    }, 5000);
    const spotInterval = setInterval(() => {
      setSpots(prev => Math.max(2, prev - (Math.random() > 0.8 ? 1 : 0)));
    }, 15000);
    const buyerInterval = setInterval(() => {
      if (Math.random() > 0.7) {
        setRecentBuyer({
          name: NAMES[Math.floor(Math.random() * NAMES.length)],
          city: ARG_CITIES[Math.floor(Math.random() * ARG_CITIES.length)]
        });
        setTimeout(() => setRecentBuyer(null), 5000);
      }
    }, 12000);
    const joinedInterval = setInterval(() => {
      setJoinedCount(prev => prev + 1);
    }, 3 * 60 * 1000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
      clearInterval(buyerInterval);
      clearInterval(joinedInterval);
    };
  }, []);
  const handleScrollToPurchase = () => {
    document.getElementById('purchase-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleAddComment = () => {
    if (!newComment.trim()) return;
    const comment = {
      id: Date.now(),
      name: 'Usuario',
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${Math.random()}`,
      date: 'Recién',
      rating: newRating,
      text: newComment,
      likes: 0
    };
    setComments([comment, ...comments]);
    setNewComment('');
    setShowSuccessModal(true);
  };
  const today = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return <div className="max-w-4xl mx-auto px-4 py-8 relative overflow-hidden font-sans" data-id="element-22">
      {/* Subtle Natural Textures */}
      <div className="fixed inset-0 pointer-events-none -z-10" data-id="element-23">
        <div className="absolute inset-0 texture-grain opacity-30" data-id="element-24" />
        <div className="absolute top-[10%] -left-20 w-96 h-96 bg-rose-100/40 organic-shape blur-3xl" data-id="element-25" />
        <div className="absolute bottom-[20%] -right-20 w-80 h-80 bg-rose-200/20 organic-shape blur-3xl" data-id="element-26" />
      </div>

      {/* Header */}
      <header id="hero" className="text-center mb-12 relative z-10" data-id="element-27">
        {/* Hero Lavender Decorations */}
        <Lavender className="-top-10 -left-10 md:left-0" scale={1.2} rotate={-15} delay={0.2} data-id="element-28" />
        <Lavender className="-top-5 -left-4 md:left-12 opacity-60" scale={0.8} rotate={-10} delay={0.5} data-id="element-29" />
        <Lavender className="-top-12 -right-10 md:right-0" scale={1.1} rotate={15} delay={0.8} data-id="element-30" />
        <Lavender className="-top-6 -right-4 md:right-12 opacity-60" scale={0.7} rotate={10} delay={1.1} data-id="element-31" />

        {/* Trust Badges - Top Discreet Line */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-nowrap justify-center items-center gap-x-2 sm:gap-x-3 mb-8 text-[7px] sm:text-[9px] uppercase tracking-[0.12em] text-gray-500 font-medium whitespace-nowrap" data-id="element-32">
          <div className="flex items-center gap-1" data-id="element-33">
            <GraduationCapIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37]" data-id="element-34" />
            <span data-id="element-35">
              <AnimatedCounter target={1000} data-id="element-36" /> alumnos
            </span>
          </div>
          <FlowerIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37] shrink-0" data-id="element-37" />
          <div className="flex items-center gap-1" data-id="element-38">
            <ZapIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37]" data-id="element-39" />
            <span data-id="element-40">Acceso inmediato</span>
          </div>
          <FlowerIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37] shrink-0" data-id="element-41" />
          <div className="flex items-center gap-1" data-id="element-42">
            <ShieldCheckIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-[#d4af37]" data-id="element-43" />
            <span data-id="element-44">Garantía 7 días</span>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="mb-8 relative pt-[0px] pb-[0px]" data-id="element-45">
          {/* Top ornament */}

          {/* "Bienvenida a" intro */}
          <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="italic text-gray-500 md:text-2xl tracking-[0.2em] font-light uppercase text-[14px] pt-[0px] pb-[0px] mt-[0px] mb-[0px]" data-id="element-46">
            Bienvenida a
          </p>

          {/* Main Title — bohemian premium */}
          <div className="relative inline-block mb-4" data-id="element-47">
            <div className="absolute inset-0 -inset-x-8 -inset-y-2 bg-gradient-radial from-rose-50/80 via-rose-50/30 to-transparent blur-2xl -z-10" aria-hidden="true" data-id="element-48" />
            <h1 style={{
            fontFamily: "'Playfair Display', serif",
            letterSpacing: '0.04em',
            textShadow: '0 1px 0 rgba(255,255,255,0.5), 0 2px 12px rgba(122,85,25,0.15)'
          }} className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight gold-deep-text relative" data-id="element-49">
              ESENCIA <span className="italic" data-id="element-50">Áurica</span>
            </h1>
          </div>

          {/* Bottom botanical ornament */}
          <div className="flex items-center justify-center gap-3 pt-[0px] pb-[0px] mt-[0px] mb-[0px]" data-id="element-51">
            <div className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent via-[#d4af37]/50 to-[#d4af37]/80" data-id="element-52" />
            <svg className="w-10 h-4 text-[#d4af37]" viewBox="0 0 40 12" fill="none" data-id="element-53">
              <path d="M2 6 Q 10 0, 20 6 T 38 6" stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.7" data-id="element-54" />
              <circle cx="20" cy="6" r="1.5" fill="currentColor" data-id="element-55" />
              <circle cx="6" cy="6" r="0.8" fill="currentColor" opacity="0.6" data-id="element-56" />
              <circle cx="34" cy="6" r="0.8" fill="currentColor" opacity="0.6" data-id="element-57" />
            </svg>
            <div className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent via-[#d4af37]/50 to-[#d4af37]/80" data-id="element-58" />
          </div>

          {/* Subtitle */}
          <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed font-light tracking-wide" data-id="element-59">
            La guía{' '}
            <span className="italic text-[#8a6d1f] font-medium" data-id="element-60">premium</span>{' '}
            para aprender a crear{' '}
            <span className="italic" data-id="element-61">perfumes naturales</span> y{' '}
            <span className="italic" data-id="element-62">brumas áuricas</span> con intención,
            energía y propósito.
          </p>
        </motion.div>
        <div className="mb-6 relative" onClick={handleScrollToPurchase} data-id="element-63">
          {/* Golden Orbs - subtle ambient particles behind the book */}
          <div className="absolute inset-0 pointer-events-none overflow-visible" data-id="element-64">
            {[{
            size: 16,
            top: '12%',
            left: '18%',
            delay: 0,
            duration: 5
          }, {
            size: 8,
            top: '25%',
            left: '8%',
            delay: 1.2,
            duration: 6
          }, {
            size: 12,
            top: '60%',
            left: '12%',
            delay: 0.6,
            duration: 4.5
          }, {
            size: 6,
            top: '78%',
            left: '22%',
            delay: 2,
            duration: 5.5
          }, {
            size: 20,
            top: '8%',
            right: '14%',
            delay: 0.3,
            duration: 5
          }, {
            size: 10,
            top: '40%',
            right: '6%',
            delay: 1.5,
            duration: 6
          }, {
            size: 14,
            top: '68%',
            right: '10%',
            delay: 0.9,
            duration: 4.8
          }, {
            size: 7,
            top: '85%',
            right: '20%',
            delay: 2.2,
            duration: 5.2
          }, {
            size: 5,
            top: '50%',
            left: '50%',
            delay: 1.8,
            duration: 4
          }, {
            size: 9,
            top: '20%',
            left: '45%',
            delay: 2.5,
            duration: 5.5
          }].map((orb, i) => <motion.div key={i} className="absolute rounded-full" style={{
            width: orb.size,
            height: orb.size,
            top: orb.top,
            left: orb.left,
            right: orb.right,
            background: 'radial-gradient(circle, rgba(252,246,186,0.95) 0%, rgba(212,175,55,0.6) 40%, rgba(191,149,63,0.2) 70%, transparent 100%)',
            boxShadow: '0 0 12px rgba(212,175,55,0.6), 0 0 24px rgba(212,175,55,0.3)'
          }} animate={{
            y: [0, -20, 0],
            opacity: [0.3, 0.9, 0.3],
            scale: [0.8, 1.1, 0.8]
          }} transition={{
            duration: orb.duration,
            delay: orb.delay,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-65" />)}
          </div>

          {/* Floating Book */}
          <motion.div className="cursor-pointer relative z-10" animate={{
          y: [0, -12, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-66">
            <img src="https://cdn.magicpatterns.com/uploads/wGfryzV7THDsyTanN3AYoH/Libro_principal_PNG_MOCKUP.png" alt="Esencia Áurica" className="w-full max-w-sm mx-auto drop-shadow-2xl" data-id="element-67" />
          </motion.div>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="relative mt-4 mb-2" data-id="element-68">
          {/* Rose gold gradient frame */}
          <div className="relative p-[1.5px] rounded-2xl" style={{
          background: 'linear-gradient(135deg, #d4a574 0%, #e8b89a 25%, #f5d5c0 50%, #e8b89a 75%, #b8865c 100%)',
          boxShadow: '0 8px 32px rgba(184,134,92,0.15), 0 2px 8px rgba(212,165,116,0.2)'
        }} data-id="element-69">
            <div className="relative rounded-2xl px-6 py-7 md:px-10 md:py-9 overflow-hidden pt-[0px] pb-[0px] mt-[0px] mb-[0px]" style={{
            background: 'linear-gradient(135deg, #fdf6ee 0%, #faeee0 50%, #f5e3d0 100%)'
          }} data-id="element-70">
              {/* Subtle corner ornaments */}
              <div className="absolute top-3 left-3 w-6 h-6 border-t border-l border-[#b8865c]/40 rounded-tl-lg" data-id="element-71" />
              <div className="absolute top-3 right-3 w-6 h-6 border-t border-r border-[#b8865c]/40 rounded-tr-lg" data-id="element-72" />
              <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[#b8865c]/40 rounded-bl-lg" data-id="element-73" />
              <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-[#b8865c]/40 rounded-br-lg" data-id="element-74" />

              <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: '#6b4513'
            }} className="text-lg md:text-2xl italic leading-relaxed tracking-wide pt-[13px] pb-[13px]" data-id="element-75">
                Imaginá todo lo que podrías crear si dejaras de{' '}
                <span className="font-semibold not-italic gold-deep-text" data-id="element-76">
                  dudar de vos
                </span>
                .
              </p>
              <div className="flex items-center justify-center gap-2 my-4 pt-[0px] pb-[0px] mt-[0px] mb-[0px]" data-id="element-77">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#b8865c]/60" data-id="element-78" />
                <div className="w-1 h-1 rounded-full bg-[#b8865c]" data-id="element-79" />
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#b8865c]/60" data-id="element-80" />
              </div>
              <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: '#6b4513'
            }} className="text-base md:text-xl leading-relaxed tracking-wide font-light" data-id="element-81">
                Esta guía nació para enseñarte a transformar tu{' '}
                <span className="italic font-medium" data-id="element-82">creatividad</span> y{' '}
                <span className="italic font-medium" data-id="element-83">pasión</span> en algo{' '}
                <span className="font-bold not-italic gold-deep-text" style={{
                letterSpacing: '0.05em'
              }} data-id="element-84">
                  REAL
                </span>
                .
              </p>
            </div>
          </div>
        </motion.div>
      </header>

      {/* Sales Copy Sections */}
      <section id="oferta" className="mb-16 relative z-10 px-2" data-id="element-85">
        {/* The Dream */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-86">
          <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="text-2xl md:text-3xl text-gray-700 italic leading-relaxed" data-id="element-87">
            Imaginás tu propia{' '}
            <span className="gold-deep-text font-bold not-italic" data-id="element-88">marca</span>.{' '}
            <br className="hidden md:block" data-id="element-89" />
            Tus propias{' '}
            <span className="gold-deep-text font-bold not-italic" data-id="element-90">
              creaciones
            </span>
            . <br className="hidden md:block" data-id="element-91" />
            Tus propios{' '}
            <span className="gold-deep-text font-bold not-italic" data-id="element-92">aromas</span>.
          </p>
          <div className="flex justify-center mt-6" data-id="element-93">
            <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#d4af37]/40 to-transparent" data-id="element-94" />
          </div>
        </motion.div>

        {/* The Reality Check */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white/30 backdrop-blur-sm p-8 md:p-12 rounded-[2.5rem] border border-rose-100 shadow-sm mb-12 relative overflow-hidden text-center" data-id="element-95">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-rose-200 to-transparent opacity-50" data-id="element-96" />
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto" data-id="element-97">
            Pero la realidad es que terminás dejando tus ideas para después…{' '}
            <br className="hidden md:block" data-id="element-98" />
            <span className="font-medium text-gray-800" data-id="element-99">
              porque nadie te enseñó el paso a paso para convertir esa pasión en
              algo real.
            </span>
          </p>
        </motion.div>

        {/* The Solution / Manifest */}
        <div className="relative" data-id="element-100">
          <Lavender className="top-1/2 -left-12 opacity-40 hidden md:block" scale={0.9} rotate={-20} data-id="element-101" />
          <Lavender className="top-1/3 -right-12 opacity-40 hidden md:block" scale={0.8} rotate={20} data-id="element-102" />

          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="relative p-8 md:p-14 rounded-[3rem] bg-gradient-to-b from-white/60 to-rose-50/40 border border-rose-200/60 shadow-xl text-center" data-id="element-103">
            {/* Decorative Rose Gold Elements */}
            <div className="absolute -top-4 -left-4 w-12 h-12 text-[#e2b4bd] opacity-40" data-id="element-104">
              <FlowerIcon className="w-full h-full" data-id="element-105" />
            </div>
            <div className="absolute -bottom-4 -right-4 w-12 h-12 text-[#e2b4bd] opacity-40 rotate-180" data-id="element-106">
              <FlowerIcon className="w-full h-full" data-id="element-107" />
            </div>

            <h3 style={{
            fontFamily: "'Playfair Display', serif"
          }} className="text-2xl md:text-3xl font-bold mb-8 gold-deep-text" data-id="element-108">
              Esencia Áurica
            </h3>

            <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-light italic mb-10" data-id="element-109">
              Fue creada para mujeres que sueñan con conectar con el mundo
              natural, crear con sus propias manos y descubrir una forma de
              expresión{' '}
              <span className="not-italic font-semibold text-[#b76e79]" data-id="element-110">
                (incluso de ingresos)
              </span>{' '}
              mucho más auténtica.
            </p>

            <motion.a href={PURCHASE_LINK} className="relative overflow-hidden px-6 py-3 rounded-full mx-auto flex items-center gap-2 text-sm md:text-base font-semibold tracking-wide shadow-lg cursor-pointer group w-fit" style={{
            background: 'linear-gradient(135deg, #d4a574 0%, #e8b89a 30%, #f5d5c0 50%, #e8b89a 70%, #b8865c 100%)',
            color: '#5a3a1a',
            border: '1px solid rgba(212, 165, 116, 0.4)'
          }} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} data-id="element-111">
              {/* Animated shine effect */}
              <motion.div className="absolute inset-0 w-full h-full" style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
              transform: 'translateX(-100%)'
            }} animate={{
              transform: ['translateX(-100%)', 'translateX(200%)']
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatDelay: 2
            }} data-id="element-112" />
              <span className="relative z-10 whitespace-nowrap" data-id="element-113">
                Empezar mi camino creativo
              </span>
              <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 relative z-10 group-hover:translate-x-1 transition-transform" data-id="element-114" />
            </motion.a>
          </motion.div>
        </div>

        {/* "Esto es para vos si..." Section */}
        <div className="mt-20 mb-16" data-id="element-115">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-116">
            <h2 style={{
            fontFamily: "'Playfair Display', serif"
          }} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-117">
              Esto es para <span className="gold-deep-text italic" data-id="element-118">vos</span>{' '}
              si...
            </h2>
            <div className="h-px w-20 bg-[#e2b4bd] mx-auto" data-id="element-119" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-120">
            {[{
            text: 'Buscás un espacio de calma y conexión a través de la creación manual.',
            icon: HeartIcon
          }, {
            text: 'Querés aprender a formular tus propios perfumes y brumas con ingredientes 100% naturales.',
            icon: FlowerIcon
          }, {
            text: 'Soñás con tener un emprendimiento con propósito o simplemente regalar algo hecho por vos.',
            icon: ZapIcon
          }, {
            text: 'Sentís que tenés creatividad pero no sabés cómo canalizarla en un proyecto real.',
            icon: StarIcon
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: i % 2 === 0 ? -20 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group p-6 rounded-2xl bg-white/40 border border-rose-100/50 hover:border-[#e8b89a]/50 transition-all duration-300 shadow-sm flex items-start gap-4" data-id="element-121">
                <div className="p-2 rounded-lg bg-gradient-to-br from-rose-50 to-white border border-rose-100 group-hover:scale-110 transition-transform duration-300" data-id="element-122">
                  <item.icon className="w-5 h-5 text-[#b8865c]" data-id="element-123" />
                </div>
                <p className="text-gray-700 leading-relaxed font-light md:text-lg" data-id="element-124">
                  {item.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* "Lo que vas a aprender" Section */}
        <div className="mt-24 mb-20 relative" data-id="element-125">
          <div className="absolute inset-0 bg-[#fdf2f2]/30 blur-3xl -z-10 rounded-full" data-id="element-126" />

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-14" data-id="element-127">
            <h2 style={{
            fontFamily: "'Playfair Display', serif"
          }} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-128">
              Lo que vas a{' '}
              <span className="gold-deep-text italic" data-id="element-129">aprender</span>
            </h2>
            <p style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} className="text-lg md:text-xl text-gray-600 italic" data-id="element-130">
              Un recorrido profundo desde la esencia hasta la creación real.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto px-4" data-id="element-131">
            {[{
            title: 'Perfumería Natural',
            text: 'Cómo crear tus propios perfumes naturales desde cero, entendiendo las notas y fijación.',
            icon: ZapIcon
          }, {
            title: 'Brumas Áuricas',
            text: 'El arte de las brumas áuricas para limpiar, proteger y armonizar tus espacios y energía.',
            icon: FlowerIcon
          }, {
            title: 'Aromaterapia Consciente',
            text: 'Secretos de la aromaterapia y el uso sagrado de aceites esenciales para el bienestar.',
            icon: ShieldCheckIcon
          }, {
            title: 'Tu Propio Proyecto',
            text: 'Cómo transformar tu pasión en un emprendimiento auténtico, rentable y con propósito.',
            icon: GraduationCapIcon
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="relative p-8 rounded-[2rem] bg-white/60 border border-rose-100/40 shadow-sm hover:shadow-md transition-shadow duration-500 group" data-id="element-132">
                <div className="absolute top-6 right-8 text-[#d4af37]/10 group-hover:text-[#d4af37]/20 transition-colors duration-500" data-id="element-133">
                  <item.icon className="w-12 h-12" data-id="element-134" />
                </div>

                <h4 style={{
              fontFamily: "'Playfair Display', serif"
            }} className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2" data-id="element-135">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#b8865c]" data-id="element-136" />
                  {item.title}
                </h4>
                <p style={{
              fontFamily: "'Cormorant Garamond', serif"
            }} className="text-gray-600 text-lg leading-relaxed" data-id="element-137">
                  {item.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* "Qué incluye la guía" Section */}
        <div className="mt-24 mb-20" data-id="element-138">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white/40 backdrop-blur-sm rounded-[3rem] border border-rose-100/60 p-8 md:p-16 shadow-2xl relative overflow-hidden" data-id="element-139">
            {/* Decorative background element */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#fdf2f2] rounded-full blur-3xl opacity-60" data-id="element-140" />

            <div className="relative z-10 max-w-3xl mx-auto" data-id="element-141">
              <div className="text-center mb-12" data-id="element-142">
                <h2 style={{
                fontFamily: "'Playfair Display', serif"
              }} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-143">
                  Qué incluye la{' '}
                  <span className="gold-deep-text italic" data-id="element-144">guía</span>
                </h2>
                <div className="h-px w-16 bg-[#d4af37]/40 mx-auto" data-id="element-145" />
              </div>

              <div className="space-y-6" data-id="element-146">
                {[{
                title: 'El despertar de los sentidos',
                text: 'Introducción profunda al mundo de la perfumería natural.'
              }, {
                title: 'Tu laboratorio sagrado',
                text: 'Herramientas y materiales básicos para comenzar tu camino.'
              }, {
                title: 'Alquimia de aromas',
                text: 'Clasificación de aceites esenciales y el arte de las notas olfativas.'
              }, {
                title: 'Recetario exclusivo',
                text: 'Fórmulas probadas para crear tus propios perfumes y brumas.'
              }, {
                title: 'Energía en movimiento',
                text: 'Rituales de intención y técnicas de limpieza energética.'
              }, {
                title: 'Tu marca auténtica',
                text: 'Guía paso a paso para transformar tu pasión en un proyecto real.'
              }, {
                title: 'Tu kit creativo',
                text: 'Recursos descargables y etiquetas editables listas para usar.'
              }].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-5 group" data-id="element-147">
                    <div className="mt-1 flex-shrink-0" data-id="element-148">
                      <div className="w-6 h-6 rounded-full border border-[#e8b89a] flex items-center justify-center group-hover:bg-[#e8b89a]/10 transition-colors duration-300" data-id="element-149">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#b8865c]" data-id="element-150" />
                      </div>
                    </div>
                    <div data-id="element-151">
                      <h4 style={{
                    fontFamily: "'Playfair Display', serif"
                  }} className="text-lg md:text-xl font-bold text-gray-800 leading-tight" data-id="element-152">
                        {item.title}
                      </h4>
                      <p style={{
                    fontFamily: "'Cormorant Garamond', serif"
                  }} className="text-gray-600 text-lg italic font-light" data-id="element-153">
                        {item.text}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>

        {/* "Sobre mí" Section */}

        {/* Quick Benefits Grid - Reduced size by 40% */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mt-8 max-w-2xl mx-auto" data-id="element-154">
          {[{
          title: 'Aceites esenciales',
          icon: '🌿'
        }, {
          title: 'Aromas naturales',
          icon: '🌸'
        }, {
          title: 'Energía e intención',
          icon: '✨'
        }, {
          title: 'Elementos holísticos',
          icon: '💎'
        }].map((item, i) => <motion.div key={i} whileHover={{
          y: -3
        }} className="flex flex-col items-center gap-1.5 p-3.5 bg-white/40 backdrop-blur-xs rounded-2xl border border-rose-50/50 shadow-sm text-center" data-id="element-155">
              <span className="text-xl" data-id="element-156">{item.icon}</span>
              <span className="text-[10px] md:text-[11px] font-semibold text-gray-500 uppercase tracking-widest leading-tight" data-id="element-157">
                {item.title}
              </span>
            </motion.div>)}
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonos" className="mb-16 relative z-10" data-id="element-158">
        <Lavender className="top-0 -right-10 opacity-30" scale={1.5} rotate={15} data-id="element-159" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16 relative" data-id="element-160">
          {/* Subtle background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-32 bg-rose-100/30 blur-3xl -z-10" data-id="element-161" />

          <span className="inline-block px-4 py-1.5 rounded-full bg-rose-50 border border-rose-100 text-[#b76e79] text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-6" data-id="element-162">
            Regalo exclusivo
          </span>

          <h2 style={{
          fontFamily: "'Playfair Display', serif"
        }} className="text-3xl md:text-5xl font-bold mb-6 text-gray-800 leading-tight" data-id="element-163">
            Expandí tu universo con <br data-id="element-164" />
            <span className="gold-deep-text italic" data-id="element-165">6 Ebooks de regalo</span>
          </h2>

          <p style={{
          fontFamily: "'Cormorant Garamond', serif"
        }} className="text-lg md:text-2xl text-gray-600 max-w-2xl mx-auto leading-relaxed italic font-light" data-id="element-166">
            Diseñados para acompañarte en cada paso y llevar tu conocimiento{' '}
            <br className="hidden md:block" data-id="element-167" />
            hacia un nivel profesional y consciente.
          </p>

          <div className="flex items-center justify-center gap-4 mt-8" data-id="element-168">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#d4af37]/40" data-id="element-169" />
            <span className="text-[#b8865c] font-bold tracking-widest text-sm uppercase" data-id="element-170">
              ¡Gratis solo por hoy!
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#d4af37]/40" data-id="element-171" />
          </div>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12" data-id="element-172">
          {[{
          url: 'https://cdn.magicpatterns.com/uploads/mFNrE3ULhWjTGC1RXEDAuv/Bono_1.png',
          title: 'Roll on Naturales',
          desc: 'Aprendé a crear mezclas terapéuticas en formato práctico para llevar con vos siempre.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/8ddifw1mPzx4Peuo9RcH9u/Bono_2.png',
          title: 'Guía de Aromaterapia',
          desc: 'Los fundamentos esenciales para entender cómo los aromas impactan en tus emociones.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/oqscyZLLhA8WkHPDPwDPEG/Bono_3.png',
          title: 'Aromas y Cristales',
          desc: 'Una combinación mágica para potenciar la energía de tus creaciones con gemas naturales.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/1CC4X4cv23jGgFF8VVp3Bp/Bono_4.png',
          title: 'Cajas Aromáticas',
          desc: 'Ideas creativas para presentar tus productos y convertirlos en el regalo perfecto.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/bYdtao36mehtsJW3aj1Q7e/Bono_5.png',
          title: 'Gráfica para Cajas',
          desc: 'Recursos visuales listos para usar que le darán un toque profesional a tu marca.'
        }, {
          url: 'https://cdn.magicpatterns.com/uploads/nZd34USunT783fHmRGw4FW/Bono_6.png',
          title: 'Guía de Costos',
          desc: 'La herramienta clave para ponerle precio a tu trabajo y hacer crecer tu proyecto.'
        }].map((bono, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="group cursor-pointer flex flex-col" onClick={handleScrollToPurchase} data-id="element-173">
              <div className="relative aspect-[4/5] overflow-hidden rounded-3xl shadow-2xl border border-rose-50/50 mb-6" data-id="element-174">
                <img src={bono.url} alt={bono.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out" data-id="element-175" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-176" />
              </div>

              <div className="px-2" data-id="element-177">
                <h4 style={{
              fontFamily: "'Playfair Display', serif"
            }} className="text-xl md:text-2xl font-bold text-gray-800 mb-2 group-hover:text-[#b8865c] transition-colors duration-300 flex items-center gap-3" data-id="element-178">
                  <span className="w-8 h-px bg-[#d4af37]/40" data-id="element-179" />
                  {bono.title}
                </h4>
                <p style={{
              fontFamily: "'Cormorant Garamond', serif"
            }} className="text-gray-600 text-lg leading-relaxed italic font-light" data-id="element-180">
                  {bono.desc}
                </p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* FAQ Section */}

      {/* Guarantee Section */}

      {/* Final Call to Action Section */}
      <section id="compra" className="mb-16 scroll-mt-20 relative z-10" data-id="element-181">
        <div id="purchase-section" className="text-white text-center py-4 px-4 rounded-t-[2.5rem] relative overflow-hidden" style={{
        background: 'linear-gradient(to right, #3d5a35, #5a7a4a, #3d5a35)',
        boxShadow: '0 -4px 20px rgba(61, 90, 53, 0.2)'
      }} data-id="element-182">
          <div className="relative z-10 flex items-center justify-center gap-3" data-id="element-183">
            <div className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" data-id="element-184" />
            <span className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold drop-shadow-sm" data-id="element-185">
              Oferta válida solo por hoy · {today}
            </span>
            <div className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" data-id="element-186" />
          </div>
        </div>
        <div className="bg-[#1a0f11] p-8 md:p-12 rounded-b-[2.5rem] shadow-2xl text-center relative overflow-hidden space-y-[16px]" data-id="element-187">
          {/* Subtle background texture */}
          <div className="absolute inset-0 opacity-10 pointer-events-none texture-grain" data-id="element-188" />

          <div className="space-y-3 relative z-10" data-id="element-189">
            <p className="text-rose-200/60 line-through text-xl font-light tracking-widest" data-id="element-190">
              Antes $49.990 ARS
            </p>
            <div className="flex flex-col items-center" data-id="element-191">
              <span className="text-rose-100/80 text-sm uppercase tracking-[0.3em] mb-1" data-id="element-192">
                Inversión única
              </span>
              <p style={{
              fontFamily: "'Playfair Display', serif"
            }} className="text-6xl md:text-8xl font-bold text-white drop-shadow-[0_2px_10px_rgba(255,255,255,0.2)]" data-id="element-193">
                $19.990{' '}
                <span className="text-2xl md:text-3xl font-light opacity-80" data-id="element-194">
                  ARS
                </span>
              </p>
            </div>
          </div>

          <div className="inline-block relative z-10" data-id="element-195">
            <div className="bg-gradient-to-r from-[#b76e79]/20 to-[#e2b4bd]/20 text-rose-100 px-4 py-1.5 rounded-full flex items-center gap-2 border border-[#b76e79]/40 backdrop-blur-sm" data-id="element-196">
              <CreditCardIcon className="w-3.5 h-3.5 text-[#e2b4bd]" data-id="element-197" />
              <span className="font-medium tracking-wide text-[10px] md:text-xs whitespace-nowrap" data-id="element-198">
                Hasta 3 cuotas sin interés
              </span>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl p-4 border border-white/10 grid grid-cols-2 gap-4 items-center relative z-10 max-w-lg mx-auto" data-id="element-199">
            <div className="flex flex-col items-center gap-1 border-r border-white/10" data-id="element-200">
              <div className="flex items-center gap-2" data-id="element-201">
                <div className="w-1.5 h-1.5 bg-[#e2b4bd] rounded-full animate-pulse shadow-[0_0_6px_#e2b4bd]" data-id="element-202"></div>
                <span className="text-rose-100/70 font-semibold text-[8px] md:text-[9px] uppercase tracking-[0.15em]" data-id="element-203">
                  Explorando
                </span>
              </div>
              <span className="text-2xl font-bold text-white" data-id="element-204">
                {viewers}{' '}
                <span className="text-xs font-light opacity-50 italic" data-id="element-205">
                  mujeres
                </span>
              </span>
            </div>

            <div className="flex flex-col items-center gap-1" data-id="element-206">
              <div className="flex items-center gap-2" data-id="element-207">
                <TicketIcon className="w-3.5 h-3.5 text-[#b76e79]" data-id="element-208" />
                <span className="text-rose-100/70 font-semibold text-[8px] md:text-[9px] uppercase tracking-[0.15em]" data-id="element-209">
                  Cupos
                </span>
              </div>
              <div className="flex flex-col items-center" data-id="element-210">
                <span className="text-2xl font-bold text-[#b76e79]" data-id="element-211">
                  {spots}
                </span>
                <p className="text-[7px] text-rose-200/40 italic" data-id="element-212">
                  Último acceso hace 5m
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-b from-white/5 to-transparent p-3 rounded-xl border border-white/5 relative z-10 max-w-[240px] mx-auto" data-id="element-213">
            <div className="text-2xl font-bold text-white mb-0.5 tracking-tighter" data-id="element-214">
              <AnimatedCounter target={joinedCount} showPlus={false} data-id="element-215" />
            </div>
            <div className="text-rose-100/60 text-[8px] uppercase tracking-[0.2em] font-medium leading-tight" data-id="element-216">
              Almas creativas ya se sumaron
            </div>
          </div>

          <motion.a href={PURCHASE_LINK} initial={{
          scale: 1
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} animate={{
          boxShadow: ['0 0 0 0 rgba(74, 222, 128, 0)', '0 0 0 15px rgba(74, 222, 128, 0.1)', '0 0 0 0 rgba(74, 222, 128, 0)']
        }} transition={{
          boxShadow: {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }} className="relative block w-full overflow-hidden group rounded-2xl" data-id="element-217">
            <div className="absolute inset-0 bg-gradient-to-r from-[#22c55e] via-[#4ade80] to-[#22c55e] transition-all duration-500 group-hover:opacity-100" style={{
            boxShadow: '0 10px 40px rgba(34, 197, 94, 0.4)'
          }} data-id="element-218" />

            {/* Shine animation */}
            <motion.div className="absolute inset-0 w-full h-full" style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            transform: 'translateX(-100%)'
          }} animate={{
            transform: ['translateX(-100%)', 'translateX(200%)']
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut',
            repeatDelay: 1
          }} data-id="element-219" />

            <div className="relative py-6 px-4 flex items-center justify-center gap-4 border border-white/20 rounded-2xl" data-id="element-220">
              <span className="text-white text-xl md:text-2xl font-black tracking-[0.08em] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" data-id="element-221">
                SÍ, QUIERO MI GUÍA AHORA
              </span>
              <ArrowRightIcon className="w-7 h-7 text-white group-hover:translate-x-2 transition-transform drop-shadow-md" data-id="element-222" />
            </div>
          </motion.a>

          <p className="text-[10px] text-rose-200/30 uppercase tracking-[0.2em] relative z-10" data-id="element-223">
            Acceso inmediato tras la compra · Pago 100% seguro
          </p>
        </div>
      </section>

      {/* Community Section */}
      <section id="comunidad" className="mb-24 relative z-10" data-id="element-224">
        <div className="bg-white/60 backdrop-blur-md p-8 rounded-[2rem] shadow-xl border border-rose-100 relative overflow-hidden" data-id="element-225">
          <div className="absolute inset-0 texture-grain opacity-10 pointer-events-none" data-id="element-226" />
          <h2 className="text-3xl font-bold mb-8 text-center relative z-10" data-id="element-227">
            Comunidad y Reseñas
          </h2>
          <div className="mb-12 space-y-4" data-id="element-228">
            <div className="flex gap-2 mb-2" data-id="element-229">
              {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setNewRating(star)} className={`transition-colors ${newRating >= star ? 'text-yellow-400' : 'text-gray-300'}`} data-id="element-230">
                  <StarIcon className={`w-8 h-8 ${newRating >= star ? 'fill-current' : ''}`} data-id="element-231" />
                </button>)}
            </div>
            <textarea className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yellow-400 outline-none min-h-[120px] bg-white" placeholder="Escribí tu reseña aquí..." value={newComment} onChange={e => setNewComment(e.target.value)} data-id="element-232" />
            <Button className="w-full md:w-auto bg-gray-900 text-white px-8 py-3 rounded-xl font-bold" onClick={handleAddComment} data-id="element-233">
              Publicar comentario
            </Button>
          </div>

          <div className="space-y-8" data-id="element-234">
            {comments.slice(0, visibleCommentsCount).map(comment => <div key={comment.id} className="flex gap-4 border-b border-gray-100 pb-8" data-id="element-235">
                <img src={comment.avatar} alt={comment.name} className="w-12 h-12 rounded-full bg-gray-100" data-id="element-236" />
                <div className="flex-1 space-y-2" data-id="element-237">
                  <div className="flex justify-between items-start" data-id="element-238">
                    <div data-id="element-239">
                      <div className="font-bold" data-id="element-240">{comment.name}</div>
                      <div className="text-xs text-gray-400" data-id="element-241">
                        {comment.date}
                      </div>
                    </div>
                    <div className="flex gap-0.5" data-id="element-242">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-3 h-3 ${i < comment.rating ? 'text-yellow-400 fill-current' : 'text-gray-200'}`} data-id="element-243" />)}
                    </div>
                  </div>
                  <p className="text-gray-700" data-id="element-244">{comment.text}</p>
                  <button className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors" data-id="element-245">
                    <HeartIcon className="w-4 h-4" data-id="element-246" /> {comment.likes}
                  </button>
                </div>
              </div>)}
          </div>

          {visibleCommentsCount < comments.length && <div className="mt-12 text-center" data-id="element-247">
              <Button className="bg-white border-2 border-gray-200 text-gray-700 px-10 py-4 rounded-2xl font-bold hover:bg-gray-50 transition-colors" onClick={() => setVisibleCommentsCount(prev => Math.min(prev + 5, comments.length))} data-id="element-248">
                Cargar 5 comentarios más
              </Button>
            </div>}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="mb-24 relative z-10 px-4" data-id="element-249">
        <Lavender className="top-1/2 -left-10 opacity-20" scale={1.3} rotate={-15} delay={1.5} data-id="element-250" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-251">
          <h2 style={{
          fontFamily: "'Playfair Display', serif"
        }} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-252">
            Preguntas <span className="gold-deep-text italic" data-id="element-253">frecuentes</span>
          </h2>
          <div className="h-px w-16 bg-[#d4af37]/40 mx-auto" data-id="element-254" />
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4" data-id="element-255">
          {[{
          q: '¿Necesito conocimientos previos?',
          a: 'No, la guía está diseñada para llevarte paso a paso, desde lo más básico hasta la creación de tus propios productos profesionales.'
        }, {
          q: '¿Cómo recibo la guía?',
          a: 'Una vez realizado el pago, recibirás un correo electrónico con el acceso inmediato para descargar la guía y todos los bonos de regalo.'
        }, {
          q: '¿Qué materiales necesito para empezar?',
          a: 'En la guía encontrarás una lista detallada, pero lo principal son aceites esenciales, alcohol de cereal y envases de vidrio. Son fáciles de conseguir.'
        }, {
          q: '¿Sirve para emprender?',
          a: '¡Totalmente! Incluimos una sección específica para ayudarte a crear tu propia marca y una guía de costos para que tu proyecto sea rentable.'
        }, {
          q: '¿Tengo garantía de compra?',
          a: 'Sí, confiamos tanto en el valor de nuestro contenido que ofrecemos una garantía de satisfacción de 7 días.'
        }].map((item, i) => <FaqItem key={i} question={item.q} answer={item.a} index={i} data-id="element-256" />)}
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="mb-24 relative z-10 px-4" data-id="element-257">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto p-8 md:p-12 rounded-[3rem] bg-gradient-to-br from-white/40 to-rose-50/30 border border-rose-100/60 shadow-xl text-center relative overflow-hidden" data-id="element-258">
          {/* Decorative background glow */}
          <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-[#d4af37]/5 rounded-full blur-3xl" data-id="element-259" />

          <div className="relative z-10" data-id="element-260">
            <div className="w-16 h-16 bg-gradient-to-br from-[#d4a574] to-[#b8865c] rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" data-id="element-261">
              <ShieldCheckIcon className="w-8 h-8 text-white" data-id="element-262" />
            </div>

            <h2 style={{
            fontFamily: "'Playfair Display', serif"
          }} className="text-2xl md:text-3xl font-bold text-gray-800 mb-6" data-id="element-263">
              Garantía de{' '}
              <span className="gold-deep-text italic" data-id="element-264">satisfacción</span>
            </h2>

            <div className="space-y-4" data-id="element-265">
              <p style={{
              fontFamily: "'Cormorant Garamond', serif"
            }} className="text-xl text-gray-700 italic leading-relaxed" data-id="element-266">
                Confiamos plenamente en el valor de nuestra guía.
              </p>
              <p style={{
              fontFamily: "'Cormorant Garamond', serif"
            }} className="text-lg md:text-xl text-gray-600 leading-relaxed" data-id="element-267">
                Si en <span className="font-bold text-[#b76e79]" data-id="element-268">7 días</span>{' '}
                sentís que no es para vos, te devolvemos el{' '}
                <span className="font-bold text-[#b76e79]" data-id="element-269">
                  100% de tu dinero
                </span>
                .
              </p>
              <div className="pt-4" data-id="element-270">
                <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold text-[#b8865c]" data-id="element-271">
                  Sin preguntas · Sin vueltas · Tu inversión está protegida
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Success Modal */}
      <AnimatePresence data-id="element-272">
        {showSuccessModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-id="element-273">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white p-8 rounded-3xl shadow-2xl max-w-sm w-full text-center space-y-6" data-id="element-274">
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto" data-id="element-275">
                <CheckCircleIcon className="w-12 h-12" data-id="element-276" />
              </div>
              <div className="space-y-2" data-id="element-277">
                <h3 className="text-2xl font-bold" data-id="element-278">
                  ¡Gracias por tu comentario!
                </h3>
                <p className="text-gray-600" data-id="element-279">
                  Tu opinión ayuda a la comunidad.
                </p>
              </div>
              <Button className="w-full bg-gray-900 text-white py-4 rounded-xl font-bold" onClick={() => setShowSuccessModal(false)} data-id="element-280">
                Cerrar
              </Button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Recent Buyer Notification */}
      <AnimatePresence data-id="element-281">
        {recentBuyer && <motion.div initial={{
        x: -100,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: -100,
        opacity: 0
      }} className="fixed bottom-6 left-6 z-40 bg-white/95 backdrop-blur p-4 rounded-2xl shadow-2xl border border-gray-100 flex items-center gap-4 max-w-xs cursor-pointer" onClick={handleScrollToPurchase} data-id="element-282">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center shrink-0" data-id="element-283">
              <UsersIcon className="w-6 h-6 text-yellow-600" data-id="element-284" />
            </div>
            <div data-id="element-285">
              <div className="text-sm font-bold" data-id="element-286">
                {recentBuyer.name} de {recentBuyer.city}
              </div>
              <div className="text-xs text-gray-500" data-id="element-287">
                Acaba de comprar Esencia Áurica 🌿
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const INITIAL_COMMENTS = [{
  id: 1,
  name: 'Romina G.',
  avatar: 'https://cdn.magicpatterns.com/uploads/t2kBtQkEaATGZkxuL5TAnk/image.png',
  date: 'hace 2 horas',
  rating: 5,
  text: 'Me llego al toque el mail con todo, ya empece a leer y es hermoso. Gracias!',
  likes: 24
}, {
  id: 2,
  name: 'Estela M.',
  avatar: 'https://cdn.magicpatterns.com/uploads/kyMxRspKrBPJM914sUPkpu/image.png',
  date: 'hace 4 horas',
  rating: 5,
  text: 'Las etiquetas editables son un fuego, me ahorraron un monton de laburo.',
  likes: 18
}, {
  id: 3,
  name: 'Julieta',
  avatar: 'https://cdn.magicpatterns.com/uploads/3usJuqgZWM4ULGThiy6Zk3/image.png',
  date: 'hace 5 horas',
  rating: 4,
  text: 'Muy bueno todo, me hubiera gustado mas recetas de perfumes amaderados pero igual esta re completo.',
  likes: 12
}, {
  id: 4,
  name: 'Carla',
  avatar: 'https://cdn.magicpatterns.com/uploads/qwQzdWdgyiEhZzEMV4tLrP/image.png',
  date: 'hace 8 horas',
  rating: 5,
  text: 'Es la guia q necesitaba para arrancar de una vez por todas. Re clara la explicacion.',
  likes: 31
}, {
  id: 5,
  name: 'Moni',
  avatar: 'https://images.unsplash.com/photo-1554151228-14d9def656e4?q=80&w=256&h=256&auto=format&fit=crop',
  date: 'hace 12 horas',
  rating: 5,
  text: 'Amo los aromas naturales y aca aprendi un monton de cosas q no sabia. Vale la pena.',
  likes: 15
}, ...Array.from({
  length: 25
}).map((_, i) => ({
  id: i + 6,
  name: `Usuario ${i + 6}`,
  avatar: `https://images.unsplash.com/photo-${['1438761681033-6461ffad8d80', '1544005313-94ddf0286df2', '1554151228-14d9def656e4', '1531746020798-e6953c6e8e04', '1506794778202-cad84cf45f1d', '1494790108377-be9c29b29330', '1517841905240-472988babdf9', '1534528741775-53994a69daeb', '1524504388940-b1c1722653e1', '1507081323142-f62f66224c9d'][i % 10]}?q=80&w=256&h=256&auto=format&fit=crop`,
  date: 'hace 1 día',
  rating: Math.random() > 0.1 ? 5 : 4,
  text: 'Excelente material, muy conforme con la compra.',
  likes: Math.floor(Math.random() * 10)
}))];